import { functions } from '@/core/firebase'
import { httpsCallable } from 'firebase/functions'

type ManagedUser = {
  uid: string
  email: string | null
}

type AccountInfo = {
  id: string
  accountName?: string
  ownerUid?: string
}

type BranchInfo = {
  id: string
  branchName?: string
  planType?: string
  accountId?: string
  currentSubscriptionId?: string | null
}

type MemberBranchInfo = BranchInfo & {
  membership?: {
    id?: string
    role?: string
  }
}

type SubscriptionInfo = {
  planType?: string | null
}

type UserDetailsPayload = {
  accounts: AccountInfo[]
  ownedBranches: BranchInfo[]
  memberBranches: MemberBranchInfo[]
  currentSubscription: SubscriptionInfo | null
}

function toArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

function getPlanDisplay(plan?: string): string {
  if (!plan) return 'unknown'
  return plan.charAt(0).toUpperCase() + plan.slice(1)
}

function showStatusBanner(
  banner: HTMLElement,
  message: string,
  type: 'success' | 'error',
): void {
  banner.classList.remove('hidden', 'bg-green-50', 'text-green-700', 'border-green-200', 'bg-red-50', 'text-red-700', 'border-red-200')
  if (type === 'success') {
    banner.classList.add('bg-green-50', 'text-green-700', 'border', 'border-green-200')
  } else {
    banner.classList.add('bg-red-50', 'text-red-700', 'border', 'border-red-200')
  }
  banner.textContent = message
}

function hideStatusBanner(banner: HTMLElement): void {
  banner.classList.add('hidden')
  banner.textContent = ''
}

function showTransientStatus(
  banner: HTMLElement,
  message: string,
  type: 'success' | 'error',
  duration = 4000,
): void {
  showStatusBanner(banner, message, type)
  window.setTimeout(() => {
    hideStatusBanner(banner)
  }, duration)
}

function getPlanBadgeClasses(plan: string): string {
  switch (plan) {
    case 'enterprise':
      return 'bg-purple-100 text-purple-800 border border-purple-200'
    case 'standard':
      return 'bg-blue-100 text-blue-800 border border-blue-200'
    default:
      return 'bg-gray-100 text-gray-800 border border-gray-200'
  }
}

function renderPlanBadge(plan: string | null | undefined): string {
  const normalizedPlan = (plan || 'free').toString()
  return `<span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${getPlanBadgeClasses(normalizedPlan)}">${getPlanDisplay(normalizedPlan)}</span>`
}

function flashField(element: HTMLElement): void {
  element.classList.add('ring-2', 'ring-indigo-400', 'ring-offset-2')
  setTimeout(() => {
    element.classList.remove('ring-2', 'ring-indigo-400', 'ring-offset-2')
  }, 800)
}

/**
 * Show manage branches modal for admin.
 */
export function showManageBranchesModal(user: ManagedUser): void {
  const modal = document.createElement('div')
  modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'
  modal.style.backdropFilter = 'blur(4px)'

  modal.innerHTML = `
    <div class="bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-4 overflow-hidden">
      <div class="p-6 md:p-8 space-y-6">
        <div class="flex items-start justify-between gap-4">
          <div class="space-y-1">
            <h2 class="text-2xl font-bold text-gray-900">Manage Branch Access</h2>
            <p class="text-sm text-gray-600">
              ${user.email || 'No email'} · <span class="font-mono text-xs md:text-sm break-all">${user.uid}</span>
            </p>
          </div>
          <button id="close-manage-branches-modal" class="text-gray-400 hover:text-gray-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div id="manage-branches-status" class="hidden px-4 py-3 rounded text-sm font-medium"></div>

        <div id="manage-branches-loading" class="flex items-center gap-3 text-gray-600">
          <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
          <span>Loading branches...</span>
        </div>

        <div id="manage-branches-error" class="hidden p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700"></div>

        <div id="manage-branches-body" class="hidden space-y-4">
          <div class="rounded-lg border border-gray-200 divide-y divide-gray-200">
            <details class="group" data-section="actions" open>
              <summary class="flex items-center justify-between gap-2 cursor-pointer px-4 py-3 text-gray-900 font-semibold bg-gray-50 hover:bg-gray-100">
                <span class="flex items-center gap-2">
                  <svg class="w-4 h-4 text-gray-500 group-open:rotate-90 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                  Actions
                </span>
                <span class="text-xs uppercase tracking-wide text-gray-500">Create & Assign</span>
              </summary>
              <div class="grid gap-4 px-4 py-4 md:grid-cols-2">
                <section class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div class="space-y-1">
                    <h3 class="text-base font-semibold text-gray-900">Create New Branch</h3>
                    <p class="text-sm text-gray-500">Give the branch a clear name so the owner can recognise it.</p>
                  </div>
                  <form id="create-branch-form" class="mt-4 space-y-4">
                    <div id="create-branch-account-wrapper"></div>
                    <div>
                      <label for="create-branch-name" class="block text-sm font-medium text-gray-700 mb-1">
                        Branch Name *
                      </label>
                      <input
                        type="text"
                        id="create-branch-name"
                        class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="e.g., Main Outlet"
                        required
                      />
                    </div>
                    <p class="text-sm text-gray-600">
                      New branches inherit the account's plan: <span id="create-branch-plan-label" class="font-semibold text-gray-900">—</span>
                    </p>
                    <div>
                      <label for="create-branch-reason" class="block text-sm font-medium text-gray-700 mb-1">
                        Reason *
                      </label>
                      <textarea
                        id="create-branch-reason"
                        rows="3"
                        class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="Why are you creating this branch?"
                        required
                      ></textarea>
                    </div>
                    <div id="create-branch-error" class="hidden text-sm text-red-600"></div>
                    <div class="flex justify-end">
                      <button
                        id="create-branch-submit"
                        type="submit"
                        class="bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Create Branch
                      </button>
                    </div>
                  </form>
                </section>

                <section class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div class="space-y-1">
                    <h3 class="text-base font-semibold text-gray-900">Add Member to Existing Branch</h3>
                    <p class="text-sm text-gray-500">Search the account and branch to link this user.</p>
                  </div>
                  <form id="add-branch-membership-form" class="mt-4 space-y-4">
                    <div>
                      <label for="add-membership-account" class="block text-sm font-medium text-gray-700 mb-1">
                        Account *
                      </label>
                      <input
                        type="text"
                        id="add-membership-account"
                        class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="Search accounts"
                        required
                      />
                      <div id="add-membership-account-results" class="hidden mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-auto text-sm"></div>
                    </div>
                    <div>
                      <label for="add-membership-branch" class="block text-sm font-medium text-gray-700 mb-1">
                        Branch *
                      </label>
                      <input
                        type="text"
                        id="add-membership-branch"
                        class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="Search branches"
                        required
                        disabled
                      />
                      <div id="add-membership-branch-results" class="hidden mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-auto text-sm"></div>
                    </div>
                    <input type="hidden" id="add-membership-branch-id-hidden" />
                    <div>
                      <label for="add-membership-reason" class="block text-sm font-medium text-gray-700 mb-1">
                        Reason *
                      </label>
                      <textarea
                        id="add-membership-reason"
                        rows="3"
                        class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="Why are you adding this user to the branch?"
                        required
                      ></textarea>
                    </div>
                    <div id="add-membership-error" class="hidden text-sm text-red-600"></div>
                    <div class="flex justify-end">
                      <button
                        id="add-membership-submit"
                        type="submit"
                        class="bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Add Membership
                      </button>
                    </div>
                  </form>
                </section>
              </div>
            </details>

            <details class="group" data-section="owned">
              <summary class="flex items-center justify-between gap-2 cursor-pointer px-4 py-3 text-gray-900 font-semibold bg-gray-50 hover:bg-gray-100">
                <span class="flex items-center gap-2">
                  <svg class="w-4 h-4 text-gray-500 group-open:rotate-90 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                  Owned Branches
                </span>
                <span class="text-xs uppercase tracking-wide text-gray-500" id="owned-branches-count">0 branches</span>
              </summary>
              <div class="px-4 py-4 space-y-3" id="owned-branches-list"></div>
            </details>

            <details class="group" data-section="membership">
              <summary class="flex items-center justify-between gap-2 cursor-pointer px-4 py-3 text-gray-900 font-semibold bg-gray-50 hover:bg-gray-100">
                <span class="flex items-center gap-2">
                  <svg class="w-4 h-4 text-gray-500 group-open:rotate-90 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                  Membership Branches
                </span>
                <span class="text-xs uppercase tracking-wide text-gray-500" id="member-branches-count">0 branches</span>
              </summary>
              <div class="px-4 py-4 space-y-3" id="member-branches-list"></div>
            </details>
          </div>
        </div>
      </div>
    </div>
  `

  document.body.appendChild(modal)

  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as Node
    if (
      !addMembershipAccountInput.contains(target)
      && addMembershipAccountResults
      && !addMembershipAccountResults.contains(target)
    ) {
      addMembershipAccountResults.classList.add('hidden')
    }
    if (
      !addMembershipBranchInput.contains(target)
      && addMembershipBranchResults
      && !addMembershipBranchResults.contains(target)
    ) {
      addMembershipBranchResults.classList.add('hidden')
    }
  }

  const closeModal = () => {
    document.removeEventListener('mousedown', handleClickOutside)
    modal.remove()
  }

  modal.addEventListener('click', (event) => {
    if (event.target === modal) {
      closeModal()
    }
  })

  modal.querySelector('#close-manage-branches-modal')?.addEventListener('click', closeModal)
  document.addEventListener('mousedown', handleClickOutside)

  const statusBanner = modal.querySelector('#manage-branches-status') as HTMLElement
  const loadingEl = modal.querySelector('#manage-branches-loading') as HTMLElement
  const errorEl = modal.querySelector('#manage-branches-error') as HTMLElement
  const bodyEl = modal.querySelector('#manage-branches-body') as HTMLElement

  const createBranchForm = modal.querySelector('#create-branch-form') as HTMLFormElement
  const createBranchAccountWrapper = modal.querySelector('#create-branch-account-wrapper') as HTMLElement
  const createBranchNameInput = modal.querySelector('#create-branch-name') as HTMLInputElement
  const createBranchReason = modal.querySelector('#create-branch-reason') as HTMLTextAreaElement
  const createBranchError = modal.querySelector('#create-branch-error') as HTMLElement
  const createBranchSubmit = modal.querySelector('#create-branch-submit') as HTMLButtonElement
  const createBranchPlanLabel = modal.querySelector('#create-branch-plan-label') as HTMLElement

  const addMembershipForm = modal.querySelector('#add-branch-membership-form') as HTMLFormElement
  const addMembershipAccountInput = modal.querySelector('#add-membership-account') as HTMLInputElement
  const addMembershipAccountResults = modal.querySelector('#add-membership-account-results') as HTMLElement
  const addMembershipBranchInput = modal.querySelector('#add-membership-branch') as HTMLInputElement
  const addMembershipBranchResults = modal.querySelector('#add-membership-branch-results') as HTMLElement
  const addMembershipBranchIdHidden = modal.querySelector('#add-membership-branch-id-hidden') as HTMLInputElement
  const addMembershipReasonInput = modal.querySelector('#add-membership-reason') as HTMLTextAreaElement
  const addMembershipError = modal.querySelector('#add-membership-error') as HTMLElement
  const addMembershipSubmit = modal.querySelector('#add-membership-submit') as HTMLButtonElement

  const ownedBranchesList = modal.querySelector('#owned-branches-list') as HTMLElement
  const memberBranchesList = modal.querySelector('#member-branches-list') as HTMLElement
  const ownedBranchesCountLabel = modal.querySelector('#owned-branches-count') as HTMLElement
  const memberBranchesCountLabel = modal.querySelector('#member-branches-count') as HTMLElement
  const ownedBranchesDetails = modal.querySelector('details[data-section="owned"]') as HTMLDetailsElement
  const membershipBranchesDetails = modal.querySelector('details[data-section="membership"]') as HTMLDetailsElement

  let latestData: UserDetailsPayload | null = null
  let accountSelectEl: HTMLSelectElement | null = null
  let inferredPlanType = 'free'
  let selectedAccountId: string | null = null
  let selectedAccountName: string | null = null
  const accountNameCache = new Map<string, string>()
  const branchAccountCache = new Map<string, string>()
  let accountSearchTimeout: number | undefined
  let branchSearchTimeout: number | undefined
  let activeAccountRequestId = 0
  let activeBranchRequestId = 0
  const listAccountsCallable = httpsCallable<{ search?: string, limit?: number }, { accounts: { id: string, accountName?: string | null }[] }>(functions, 'listAccounts')
  const listBranchesCallable = httpsCallable<{ accountId: string, search?: string, limit?: number }, { branches: { id: string, branchName?: string | null, planType?: string | null, accountId: string }[] }>(functions, 'listBranchesForAccount')

  function updatePlanLabel(plan: string | null | undefined): void {
    inferredPlanType = typeof plan === 'string' && plan.length > 0 ? plan : 'free'
    if (createBranchPlanLabel) {
      createBranchPlanLabel.textContent = getPlanDisplay(inferredPlanType)
    }
  }

  function setBranchSelection({ accountId, accountName, branchId, branchName }: { accountId: string, accountName: string, branchId: string, branchName: string }): void {
    selectedAccountId = accountId
    selectedAccountName = accountName
    accountNameCache.set(accountId, accountName)
    branchAccountCache.set(branchId, accountId)
    addMembershipAccountInput.value = accountName
    addMembershipBranchInput.disabled = false
    addMembershipBranchInput.value = branchName
    addMembershipBranchIdHidden.value = branchId
    addMembershipAccountResults.classList.add('hidden')
    addMembershipBranchResults.classList.add('hidden')
    addMembershipBranchResults.innerHTML = ''
    flashField(addMembershipBranchInput)
    addMembershipBranchInput.focus()
    if (typeof addMembershipForm.scrollIntoView === 'function') {
      addMembershipForm.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  function renderAccountSelect(accounts: AccountInfo[]): void {
    if (accounts.length === 0) {
      createBranchAccountWrapper.innerHTML = `
        <div class="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-700">
          No owner account found for this user. Branch creation is disabled.
        </div>
      `
      accountSelectEl = null
      createBranchSubmit.disabled = true
      return
    }

    createBranchSubmit.disabled = false

    const options = accounts.map((account) => {
      const displayName = account.accountName || `Account ${account.id}`
      accountNameCache.set(account.id, displayName)
      return `<option value="${account.id}">${displayName}</option>`
    }).join('')

    createBranchAccountWrapper.innerHTML = `
      <div>
        <label for="create-branch-account" class="block text-sm font-medium text-gray-700 mb-1">
          Account *
        </label>
        <select
          id="create-branch-account"
          class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        >
          ${options}
        </select>
      </div>
    `

    accountSelectEl = createBranchAccountWrapper.querySelector('#create-branch-account') as HTMLSelectElement
  }

  function seedLocalCaches(payload: UserDetailsPayload): void {
    accountNameCache.clear()
    branchAccountCache.clear()

    payload.accounts.forEach((account) => {
      accountNameCache.set(account.id, account.accountName || account.id)
    })

    payload.ownedBranches.forEach((branch) => {
      if (branch.accountId) {
        branchAccountCache.set(branch.id, branch.accountId)
      }
    })

    payload.memberBranches.forEach((branch) => {
      if (branch.accountId) {
        branchAccountCache.set(branch.id, branch.accountId)
      }
    })
  }

  function renderOwnedBranches(ownedBranches: BranchInfo[]): void {
    ownedBranchesList.innerHTML = ''
    if (ownedBranchesCountLabel) {
      ownedBranchesCountLabel.textContent = `${ownedBranches.length} ${ownedBranches.length === 1 ? 'branch' : 'branches'}`
    }

    if (ownedBranches.length === 0) {
      ownedBranchesList.innerHTML = `
        <div class="p-5 bg-gray-50 border border-dashed border-gray-200 rounded-lg text-sm text-gray-600">
          This user does not own any branches yet.
        </div>
      `
      ownedBranchesDetails?.removeAttribute('open')
      return
    }


    ownedBranches.forEach((branch) => {
      const item = document.createElement('div')
      item.className = 'bg-white border border-gray-200 rounded-lg p-4 shadow-sm'
      const accountId = branch.accountId || branchAccountCache.get(branch.id)
      const accountLabel = accountId
        ? accountNameCache.get(accountId) || accountId
        : 'unknown'
      item.innerHTML = `
        <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div class="space-y-2">
            <div class="flex items-center gap-2">
              <span class="text-sm font-semibold text-gray-900">${branch.branchName || 'Unnamed Branch'}</span>
              <span class="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-medium">Owner</span>
            </div>
            <div class="text-xs text-gray-600 space-y-1">
              <div><span class="font-medium text-gray-600">Plan:</span> ${renderPlanBadge(branch.planType || inferredPlanType)}</div>
              <div><span class="font-medium text-gray-600">Account:</span> <span class="text-gray-700">${accountLabel}</span></div>
            </div>
          </div>
          <div class="flex flex-wrap gap-2 md:justify-end">
            <button
              type="button"
              class="px-3 py-1.5 bg-red-50 text-red-600 border border-red-200 rounded-lg text-sm font-medium hover:bg-red-100"
              data-action="delete-branch"
            >
              Delete Branch
            </button>
          </div>
        </div>
      `

      const deleteBtn = item.querySelector('[data-action="delete-branch"]') as HTMLButtonElement | null
      deleteBtn?.addEventListener('click', () => {
        openDeleteBranchDialog(branch)
      })

      ownedBranchesList.appendChild(item)
    })
  }

  function renderMemberBranches(memberBranches: MemberBranchInfo[]): void {
    memberBranchesList.innerHTML = ''
    if (memberBranchesCountLabel) {
      memberBranchesCountLabel.textContent = `${memberBranches.length} ${memberBranches.length === 1 ? 'branch' : 'branches'}`
    }

    if (memberBranches.length === 0) {
      memberBranchesList.innerHTML = `
        <div class="p-5 bg-gray-50 border border-dashed border-gray-200 rounded-lg text-sm text-gray-600">
          This user is not a member of any other branches.
        </div>
      `
      membershipBranchesDetails?.removeAttribute('open')
      return
    }


    memberBranches.forEach((branch) => {
      const roleLabel = branch.membership?.role || 'member'
      const item = document.createElement('div')
      item.className = 'bg-white border border-gray-200 rounded-lg p-4 shadow-sm'
      const accountId = branch.accountId || branchAccountCache.get(branch.id)
      const accountLabel = accountId
        ? accountNameCache.get(accountId) || accountId
        : 'unknown'
      item.innerHTML = `
        <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div class="space-y-2">
            <div class="flex items-center gap-2">
              <span class="text-sm font-semibold text-gray-900">${branch.branchName || 'Unnamed Branch'}</span>
              <span class="inline-flex items-center px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-medium capitalize">${roleLabel}</span>
            </div>
            <div class="text-xs text-gray-600 space-y-1">
              <div><span class="font-medium text-gray-600">Plan:</span> ${renderPlanBadge(branch.planType || inferredPlanType)}</div>
              <div><span class="font-medium text-gray-600">Account:</span> <span class="text-gray-700">${accountLabel}</span></div>
            </div>
          </div>
          <div class="flex flex-wrap gap-2 md:justify-end">
            <button
              type="button"
              class="px-3 py-1.5 bg-red-50 text-red-600 border border-red-200 rounded-lg text-sm font-medium hover:bg-red-100"
              data-action="remove-membership"
            >
              Remove Membership
            </button>
          </div>
        </div>
      `

      const removeBtn = item.querySelector('[data-action="remove-membership"]') as HTMLButtonElement | null
      removeBtn?.addEventListener('click', () => {
        openRemoveMembershipDialog(branch)
      })

      memberBranchesList.appendChild(item)
    })
  }

  function showAccountResultsPlaceholder(message: string): void {
    addMembershipAccountResults.innerHTML = `<div class="px-3 py-2 text-sm text-gray-500">${message}</div>`
    addMembershipAccountResults.classList.remove('hidden')
  }

  function showBranchResultsPlaceholder(message: string): void {
    addMembershipBranchResults.innerHTML = `<div class="px-3 py-2 text-sm text-gray-500">${message}</div>`
    addMembershipBranchResults.classList.remove('hidden')
  }

  function scheduleAccountSearch(query: string): void {
    window.clearTimeout(accountSearchTimeout)
    accountSearchTimeout = window.setTimeout(() => {
      void runAccountSearch(query)
    }, 250)
  }

  async function runAccountSearch(query: string): Promise<void> {
    const requestId = ++activeAccountRequestId
    const trimmed = query.trim()

    if (!trimmed && accountNameCache.size === 0) {
      showAccountResultsPlaceholder('Type to search accounts')
      return
    }

    showAccountResultsPlaceholder('Searching accounts…')

    try {
      const response = await listAccountsCallable({ search: trimmed, limit: 10 })
      if (requestId !== activeAccountRequestId) return

      const accounts = Array.isArray(response.data?.accounts)
        ? [...response.data.accounts]
        : []

      accounts.sort((a, b) => {
        const nameA = (a.accountName || a.id).toLowerCase()
        const nameB = (b.accountName || b.id).toLowerCase()
        return nameA.localeCompare(nameB)
      })

      if (accounts.length === 0) {
        showAccountResultsPlaceholder(trimmed ? 'No accounts found.' : 'No accounts available.')
        return
      }

      addMembershipAccountResults.innerHTML = accounts.map((account) => {
        const displayName = account.accountName || account.id
        accountNameCache.set(account.id, displayName)
        return `
          <button
            type="button"
            class="w-full text-left px-3 py-2 hover:bg-indigo-50"
            data-account-id="${account.id}"
            data-account-name="${displayName}"
          >
            <div class="text-sm font-medium text-gray-900">${displayName}</div>
            <div class="text-xs text-gray-500 font-mono">${account.id}</div>
          </button>
        `
      }).join('')

      addMembershipAccountResults.classList.remove('hidden')

      addMembershipAccountResults
        .querySelectorAll<HTMLButtonElement>('button[data-account-id]')
        .forEach((button) => {
          button.addEventListener('click', () => {
            const accountId = button.getAttribute('data-account-id') || ''
            const accountName = button.getAttribute('data-account-name') || accountId
            if (!accountId) return
            selectedAccountId = accountId
            selectedAccountName = accountName
            accountNameCache.set(accountId, accountName)
            addMembershipAccountInput.value = accountName
            addMembershipAccountResults.classList.add('hidden')
            addMembershipBranchInput.disabled = false
            addMembershipBranchInput.value = ''
            addMembershipBranchIdHidden.value = ''
            addMembershipBranchResults.innerHTML = ''
            addMembershipBranchResults.classList.add('hidden')
            scheduleBranchSearch('')
          })
        })
    } catch (error) {
      console.error('[Manage Branches] Failed to search accounts:', error)
      if (requestId !== activeAccountRequestId) return
      showAccountResultsPlaceholder('Failed to load accounts. Try again.')
    }
  }

  function scheduleBranchSearch(query: string): void {
    window.clearTimeout(branchSearchTimeout)
    branchSearchTimeout = window.setTimeout(() => {
      void runBranchSearch(query)
    }, 250)
  }

  async function runBranchSearch(query: string): Promise<void> {
    if (!selectedAccountId) {
      showBranchResultsPlaceholder('Select an account first.')
      return
    }

    const requestId = ++activeBranchRequestId
    const trimmed = query.trim()
    showBranchResultsPlaceholder('Searching branches…')

    try {
      const response = await listBranchesCallable({
        accountId: selectedAccountId,
        search: trimmed,
        limit: 10,
      })

      if (requestId !== activeBranchRequestId) return

      const branches = Array.isArray(response.data?.branches)
        ? [...response.data.branches]
        : []

      branches.sort((a, b) => {
        const nameA = (a.branchName || a.id).toLowerCase()
        const nameB = (b.branchName || b.id).toLowerCase()
        return nameA.localeCompare(nameB)
      })

      if (branches.length === 0) {
        showBranchResultsPlaceholder(trimmed ? 'No branches found.' : 'No branches for this account.')
        return
      }

      addMembershipBranchResults.innerHTML = branches.map((branch) => {
        const displayName = branch.branchName || branch.id
        branchAccountCache.set(branch.id, branch.accountId)
        if (!accountNameCache.has(branch.accountId) && selectedAccountName) {
          accountNameCache.set(branch.accountId, selectedAccountName)
        }
        return `
          <button
            type="button"
            class="w-full text-left px-3 py-2 hover:bg-indigo-50"
            data-branch-id="${branch.id}"
            data-branch-name="${displayName}"
          >
            <div class="text-sm font-medium text-gray-900">${displayName}</div>
            <div class="text-xs text-gray-500 font-mono">${branch.id}</div>
          </button>
        `
      }).join('')

      addMembershipBranchResults.classList.remove('hidden')

      addMembershipBranchResults
        .querySelectorAll<HTMLButtonElement>('button[data-branch-id]')
        .forEach((button) => {
          button.addEventListener('click', () => {
            const branchId = button.getAttribute('data-branch-id') || ''
            const branchName = button.getAttribute('data-branch-name') || branchId
            if (!branchId) return
            addMembershipBranchInput.value = branchName
            addMembershipBranchIdHidden.value = branchId
            addMembershipBranchResults.classList.add('hidden')
          })
        })
    } catch (error) {
      console.error('[Manage Branches] Failed to search branches:', error)
      if (requestId !== activeBranchRequestId) return
      showBranchResultsPlaceholder('Failed to load branches. Try again.')
    }
  }

  async function loadData(): Promise<void> {
    hideStatusBanner(statusBanner)
    errorEl.classList.add('hidden')
    errorEl.textContent = ''
    bodyEl.classList.add('hidden')
    loadingEl.classList.remove('hidden')

    try {
      const callable = httpsCallable<{ userId: string }, any>(functions, 'getUserDetails')
      const response = await callable({ userId: user.uid })
      const data = response.data ?? {}
      const payload: UserDetailsPayload = {
        accounts: toArray<AccountInfo>(data.accounts),
        ownedBranches: toArray<BranchInfo>(data.ownedBranches),
        memberBranches: toArray<MemberBranchInfo>(data.memberBranches),
        currentSubscription: data.currentSubscription ?? null,
      }

      latestData = payload

      const planCandidates: Array<string | null | undefined> = [
        payload.currentSubscription?.planType ?? null,
        ...payload.ownedBranches.map((branch) => branch.planType ?? null),
        ...payload.memberBranches.map((branch) => branch.planType ?? null),
      ]
      const derivedPlan = planCandidates.find((plan): plan is string => typeof plan === 'string' && plan.length > 0) ?? 'free'
      updatePlanLabel(derivedPlan)
      seedLocalCaches(payload)
      selectedAccountId = null
      selectedAccountName = null
      addMembershipAccountInput.value = ''
      addMembershipBranchInput.value = ''
      addMembershipBranchInput.disabled = true
      addMembershipBranchIdHidden.value = ''
      addMembershipAccountResults.classList.add('hidden')
      addMembershipAccountResults.innerHTML = ''
      addMembershipBranchResults.classList.add('hidden')
      addMembershipBranchResults.innerHTML = ''

      renderAccountSelect(payload.accounts)
      renderOwnedBranches(payload.ownedBranches)
      renderMemberBranches(payload.memberBranches)

      loadingEl.classList.add('hidden')
      bodyEl.classList.remove('hidden')
    } catch (error) {
      loadingEl.classList.add('hidden')
      errorEl.classList.remove('hidden')
      errorEl.textContent = error instanceof Error ? error.message : 'Failed to load branches.'
      console.error('[Manage Branches] Failed to load data:', error)
    }
  }

  async function handleCreateBranch(): Promise<void> {
    if (!latestData) return
    const branchName = createBranchNameInput.value.trim()
    const reason = createBranchReason.value.trim()
    const accountId = accountSelectEl?.value || latestData.accounts[0]?.id

    if (!branchName || !reason || !accountId) {
      createBranchError.classList.remove('hidden')
      createBranchError.textContent = 'Branch name, account, and reason are required.'
      return
    }

    createBranchError.classList.add('hidden')
    createBranchSubmit.disabled = true
    createBranchSubmit.textContent = 'Creating...'

    try {
      const callable = httpsCallable<CreateBranchForUserPayload, any>(functions, 'createBranchForUser')
      await callable({
        targetUserId: user.uid,
        branchName,
        reason,
        accountId,
      })

      showStatusBanner(statusBanner, 'Branch created successfully.', 'success')
      createBranchForm.reset()
      updatePlanLabel(inferredPlanType)
      document.dispatchEvent(new CustomEvent('admin-user-branches-updated'))
      await loadData()
      ownedBranchesDetails?.setAttribute('open', '')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create branch.'
      createBranchError.classList.remove('hidden')
      createBranchError.textContent = message
      showStatusBanner(statusBanner, message, 'error')
      console.error('[Manage Branches] Failed to create branch:', error)
    } finally {
      createBranchSubmit.disabled = false
      createBranchSubmit.textContent = 'Create Branch'
    }
  }

  async function handleAddMembership(): Promise<void> {
    const accountId = selectedAccountId
    const branchId = addMembershipBranchIdHidden.value.trim()
    const reason = addMembershipReasonInput.value.trim()

    if (!accountId) {
      addMembershipError.classList.remove('hidden')
      addMembershipError.textContent = 'Select an account before adding a membership.'
      return
    }

    if (!branchId || !reason) {
      addMembershipError.classList.remove('hidden')
      addMembershipError.textContent = 'Branch and reason are required.'
      return
    }

    addMembershipError.classList.add('hidden')
    addMembershipSubmit.disabled = true
    addMembershipSubmit.textContent = 'Adding...'

    try {
      const callable = httpsCallable<AddBranchMembershipPayload, any>(functions, 'addUserToBranchMembership')
      await callable({
        targetUserId: user.uid,
        branchId,
        reason,
      })

      showStatusBanner(statusBanner, 'Membership added successfully.', 'success')
      addMembershipForm.reset()
      selectedAccountId = null
      selectedAccountName = null
      addMembershipBranchInput.disabled = true
      addMembershipBranchResults.classList.add('hidden')
      addMembershipAccountResults.classList.add('hidden')
      addMembershipBranchIdHidden.value = ''
      document.dispatchEvent(new CustomEvent('admin-user-branches-updated'))
      await loadData()
      membershipBranchesDetails?.setAttribute('open', '')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to add membership.'
      addMembershipError.classList.remove('hidden')
      addMembershipError.textContent = message
      showStatusBanner(statusBanner, message, 'error')
      console.error('[Manage Branches] Failed to add membership:', error)
    } finally {
      addMembershipSubmit.disabled = false
      addMembershipSubmit.textContent = 'Add Membership'
    }
  }

  function openDeleteBranchDialog(branch: BranchInfo): void {
    const overlay = document.createElement('div')
    overlay.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'
    overlay.style.backdropFilter = 'blur(4px)'

    overlay.innerHTML = `
      <div class="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 overflow-hidden">
        <form id="confirm-delete-branch-form" class="p-6 space-y-4">
          <div>
            <h3 class="text-xl font-bold text-gray-900">Delete Branch and All Data</h3>
            <p class="text-sm text-gray-600 mt-2">
              Branch <span class="font-semibold">${branch.branchName || branch.id}</span> will be permanently deleted along with all related data. This action cannot be undone.
            </p>
          </div>

          <div>
            <label class="inline-flex items-start gap-2 text-sm text-red-700 font-medium">
              <input type="checkbox" id="confirm-delete-branch-checkbox" class="mt-1 h-4 w-4 text-red-600 border-gray-300 rounded">
              Delete branch and all associated data permanently.
            </label>
          </div>

          <div>
            <label for="confirm-delete-branch-reason" class="block text-sm font-medium text-gray-700 mb-1">
              Reason *
            </label>
            <textarea
              id="confirm-delete-branch-reason"
              rows="3"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="Provide a reason for deleting this branch."
              required
            ></textarea>
          </div>

          <div id="confirm-delete-branch-error" class="hidden text-sm text-red-600"></div>

          <div class="flex justify-end gap-3">
            <button type="button" id="cancel-delete-branch" class="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
              Cancel
            </button>
            <button
              type="submit"
              id="confirm-delete-branch-submit"
              class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled
            >
              Delete Branch
            </button>
          </div>
        </form>
      </div>
    `

    document.body.appendChild(overlay)

    const form = overlay.querySelector('#confirm-delete-branch-form') as HTMLFormElement
    const checkbox = overlay.querySelector('#confirm-delete-branch-checkbox') as HTMLInputElement
    const reasonInput = overlay.querySelector('#confirm-delete-branch-reason') as HTMLTextAreaElement
    const errorBox = overlay.querySelector('#confirm-delete-branch-error') as HTMLElement
    const submitBtn = overlay.querySelector('#confirm-delete-branch-submit') as HTMLButtonElement
    const cancelBtn = overlay.querySelector('#cancel-delete-branch') as HTMLButtonElement

    const closeOverlay = () => overlay.remove()

    checkbox.addEventListener('change', () => {
      submitBtn.disabled = !checkbox.checked || reasonInput.value.trim().length === 0
    })

    reasonInput.addEventListener('input', () => {
      submitBtn.disabled = !checkbox.checked || reasonInput.value.trim().length === 0
    })

    cancelBtn.addEventListener('click', closeOverlay)

    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) {
        closeOverlay()
      }
    })

    form.addEventListener('submit', async (event) => {
      event.preventDefault()
      const reason = reasonInput.value.trim()
      if (!checkbox.checked || !reason) {
        errorBox.classList.remove('hidden')
        errorBox.textContent = 'Please confirm deletion and provide a reason.'
        return
      }

      submitBtn.disabled = true
      submitBtn.textContent = 'Deleting...'
      errorBox.classList.add('hidden')

      try {
        const callable = httpsCallable<RemoveBranchPayload, any>(functions, 'removeBranchOrMembership')
        await callable({
          targetUserId: user.uid,
          branchId: branch.id,
          deleteBranchAndData: true,
          reason,
        })

        showStatusBanner(statusBanner, 'Branch deleted successfully.', 'success')
        document.dispatchEvent(new CustomEvent('admin-user-branches-updated'))
        closeOverlay()
        await loadData()
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to delete branch.'
        errorBox.classList.remove('hidden')
        errorBox.textContent = message
        submitBtn.disabled = false
        submitBtn.textContent = 'Delete Branch'
        console.error('[Manage Branches] Failed to delete branch:', error)
        showStatusBanner(statusBanner, message, 'error')
      }
    })
  }

  function openRemoveMembershipDialog(branch: MemberBranchInfo): void {
    const overlay = document.createElement('div')
    overlay.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'
    overlay.style.backdropFilter = 'blur(4px)'

    overlay.innerHTML = `
      <div class="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 overflow-hidden">
        <form id="confirm-remove-membership-form" class="p-6 space-y-4">
          <div>
            <h3 class="text-xl font-bold text-gray-900">Remove Branch Membership</h3>
            <p class="text-sm text-gray-600 mt-2">
              Remove ${user.email || user.uid} from branch <span class="font-semibold">${branch.branchName || branch.id}</span>.
            </p>
          </div>
          <div>
            <label for="confirm-remove-membership-reason" class="block text-sm font-medium text-gray-700 mb-1">
              Reason *
            </label>
            <textarea
              id="confirm-remove-membership-reason"
              rows="3"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="Provide a reason for removing this membership."
              required
            ></textarea>
          </div>

          <div id="confirm-remove-membership-error" class="hidden text-sm text-red-600"></div>

          <div class="flex justify-end gap-3">
            <button type="button" id="cancel-remove-membership" class="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
              Cancel
            </button>
            <button
              type="submit"
              id="confirm-remove-membership-submit"
              class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Remove Membership
            </button>
          </div>
        </form>
      </div>
    `

    document.body.appendChild(overlay)

    const form = overlay.querySelector('#confirm-remove-membership-form') as HTMLFormElement
    const reasonInput = overlay.querySelector('#confirm-remove-membership-reason') as HTMLTextAreaElement
    const errorBox = overlay.querySelector('#confirm-remove-membership-error') as HTMLElement
    const submitBtn = overlay.querySelector('#confirm-remove-membership-submit') as HTMLButtonElement
    const cancelBtn = overlay.querySelector('#cancel-remove-membership') as HTMLButtonElement

    const closeOverlay = () => overlay.remove()

    cancelBtn.addEventListener('click', closeOverlay)
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) {
        closeOverlay()
      }
    })

    form.addEventListener('submit', async (event) => {
      event.preventDefault()
      const reason = reasonInput.value.trim()
      if (!reason) {
        errorBox.classList.remove('hidden')
        errorBox.textContent = 'Reason is required.'
        return
      }

      submitBtn.disabled = true
      submitBtn.textContent = 'Removing...'
      errorBox.classList.add('hidden')

      try {
        const callable = httpsCallable<RemoveBranchPayload, any>(functions, 'removeBranchOrMembership')
        await callable({
          targetUserId: user.uid,
          branchId: branch.id,
          deleteBranchAndData: false,
          reason,
        })

        showStatusBanner(statusBanner, 'Membership removed successfully.', 'success')
        document.dispatchEvent(new CustomEvent('admin-user-branches-updated'))
        closeOverlay()
        await loadData()
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to remove membership.'
        errorBox.classList.remove('hidden')
        errorBox.textContent = message
        submitBtn.disabled = false
        submitBtn.textContent = 'Remove Membership'
        console.error('[Manage Branches] Failed to remove membership:', error)
        showStatusBanner(statusBanner, message, 'error')
      }
    })
  }

  addMembershipAccountInput.addEventListener('input', (event) => {
    const value = (event.target as HTMLInputElement).value
    selectedAccountId = null
    selectedAccountName = null
    addMembershipBranchInput.disabled = true
    addMembershipBranchInput.value = ''
    addMembershipBranchIdHidden.value = ''
    addMembershipError.classList.add('hidden')
    scheduleAccountSearch(value)
  })

  addMembershipAccountInput.addEventListener('focus', () => {
    scheduleAccountSearch(addMembershipAccountInput.value)
  })

  addMembershipBranchInput.addEventListener('input', (event) => {
    const value = (event.target as HTMLInputElement).value
    addMembershipBranchIdHidden.value = ''
    addMembershipError.classList.add('hidden')
    scheduleBranchSearch(value)
  })

  addMembershipBranchInput.addEventListener('focus', () => {
    if (!addMembershipBranchInput.disabled) {
      scheduleBranchSearch(addMembershipBranchInput.value)
    }
  })

  addMembershipBranchInput.addEventListener('blur', () => {
    window.setTimeout(() => {
      addMembershipBranchResults.classList.add('hidden')
    }, 150)
  })

  addMembershipBranchInput.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      addMembershipBranchResults.classList.add('hidden')
      addMembershipBranchInput.blur()
    }
  })

  addMembershipAccountInput.addEventListener('blur', () => {
    window.setTimeout(() => {
      addMembershipAccountResults.classList.add('hidden')
    }, 150)
  })

  addMembershipAccountInput.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      addMembershipAccountResults.classList.add('hidden')
      addMembershipAccountInput.blur()
    }
  })

  createBranchForm.addEventListener('submit', (event) => {
    event.preventDefault()
    void handleCreateBranch()
  })

  addMembershipForm.addEventListener('submit', (event) => {
    event.preventDefault()
    void handleAddMembership()
  })

  void loadData()
}

type CreateBranchForUserPayload = {
  targetUserId: string
  branchName: string
  reason: string
  accountId: string
}

type AddBranchMembershipPayload = {
  targetUserId: string
  branchId: string
  reason: string
}

type RemoveBranchPayload = {
  targetUserId: string
  branchId: string
  deleteBranchAndData: boolean
  reason: string
}
